import { apiRequest } from './http';
import type {
  Project,
  ProjectListResponse,
  ProjectMembersResponse,
  ProjectStage,
} from './types';
import type { StageFilterValue } from '@/lib/stageFilter';

// §5.2 GET /api/projects
export type ProjectSortKey = 'created' | 'name';
export type ProjectSortDir = 'asc' | 'desc';

export interface ProjectsQuery {
  grant_for?: string;
  stage?: ProjectStage | '전체' | '' | StageFilterValue[];
  ua?: '전체' | '운영' | '중단' | '';
  mine?: boolean;
  linked?: boolean;
  q?: string;
  page?: number;
  page_size?: number;
  sort_key?: ProjectSortKey;
  sort_dir?: ProjectSortDir;
}

export function fetchProjects(
  query: ProjectsQuery = {},
  signal?: AbortSignal,
): Promise<ProjectListResponse> {
  return apiRequest<ProjectListResponse>('/projects', {
    query: { ...query, stage: Array.isArray(query.stage) ? query.stage.join(',') : query.stage },
    signal,
  });
}

// §5.3 GET /api/projects/{project_id} — 5자리 숫자 ID 기반 (utils.apps.project_id)
export function fetchProject(projectId: string, signal?: AbortSignal): Promise<Project> {
  return apiRequest<Project>(`/projects/${encodeURIComponent(projectId)}`, { signal });
}

// §5.4 GET /api/projects/{project_id}/members
export function fetchProjectMembers(
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectMembersResponse> {
  return apiRequest<ProjectMembersResponse>(
    `/projects/${encodeURIComponent(projectId)}/members`,
    { signal },
  );
}

// admin 즉시 INSERT — utils.apps (BIGQUERY_APPS_TABLE)에 새 row.
// 비-admin은 createProjectCreateRequest로 request flow를 거친다.
export interface AdminCreateProjectBody {
  display_name: string;
  app_match_name: string;
  gcp_project_id?: string | null;
  stores: Array<{
    code: string;
    // 번들 ID는 플랫폼(store)별 값 — 플랫폼별 정보에서 입력한다.
    bundle_id?: string | null;
    store_id?: string | null;
    sku?: string | null;
    ga_game_id?: string | null;
    tapjoy_app_id?: string | null;
    project_sub_id?: string | null;
    state_code?: string | null;
  }>;
  ga_game_id?: string | null;
  tapjoy_app_id?: string | null;
  adjust_app_token?: string | null;
  publisher?: string | null;
  company?: string | null;
  dashboard?: string | null;
}

// admin 직접 생성과 비-admin 요청이 공유하는 canonical payload 형태.
export type ProjectCreatePayload = AdminCreateProjectBody;

export interface AdminCreateProjectResponse {
  project_id: string;
  display_name: string | null;
}

export function adminCreateProject(
  body: AdminCreateProjectBody,
): Promise<AdminCreateProjectResponse> {
  return apiRequest<AdminCreateProjectResponse>('/projects', {
    method: 'POST',
    body,
  });
}

// admin 즉시 UPDATE — common + platforms 변경 사항 dict를 body로 전달.
export interface AdminUpdateProjectBody {
  common?: Record<string, string | number | null>;
  platforms?: Record<string, Record<string, string | null>>;
}

export interface AdminUpdateProjectResponse {
  project_id: string;
  status: 'done';
}

export function adminUpdateProject(
  projectId: string,
  body: AdminUpdateProjectBody,
): Promise<AdminUpdateProjectResponse> {
  return apiRequest<AdminUpdateProjectResponse>(
    `/projects/${encodeURIComponent(projectId)}`,
    { method: 'PATCH', body },
  );
}

// admin 전용: 프로젝트 아이콘 업로드 — 모든 OS에 동일 이미지를 PNG로 GCS에 저장.
// imageBase64는 data URL(data:image/...;base64,...) 또는 순수 base64.
export function uploadProjectIcon(
  projectId: string,
  imageBase64: string,
): Promise<{ project_id: string; uploaded: string[] }> {
  return apiRequest<{ project_id: string; uploaded: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/icon`,
    { method: 'POST', body: { image_base64: imageBase64 } },
  );
}

// admin 전용: 기존 project에 새 platform(OS) row 추가. 같은 store 중복은 409.
export function adminAddPlatform(
  projectId: string,
  code: string,
): Promise<{ project_id: string; code: string }> {
  return apiRequest<{ project_id: string; code: string }>(
    `/projects/${encodeURIComponent(projectId)}/platforms`,
    { method: 'POST', body: { code } },
  );
}
